import { NetworkStatus } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import { SearchQueryOrder, SearchQueryRequestedPage } from "types/generated";

import {
  API_KEY_SESSIONS_WITH_ACCESS_TO_SPACE,
  API_KEY_SESSIONS_WITH_ACCESS_TO_SPACE_COUNT,
} from "./gql";
import { SpaceDetailsApiKeySession } from "./types";

type UseSpaceDetailsApiKeySessionsProps = {
  spaceId: string;
  orderBy: SearchQueryOrder;
  first: number;
  fullTextSearch: string | null;
};

const useSpaceDetailsApiKeySessions = ({
  spaceId,
  orderBy,
  first,
  fullTextSearch,
}: UseSpaceDetailsApiKeySessionsProps) => {
  const [requestedPage, setRequestedPage] = useState<SearchQueryRequestedPage | null>(null);
  const [after, setAfter] = useState<string | null>(null);

  const resetKey = `${spaceId}:${orderBy.field}:${orderBy.direction}:${fullTextSearch ?? ""}`;
  const [previousResetKey, setPreviousResetKey] = useState(resetKey);

  if (previousResetKey !== resetKey) {
    setPreviousResetKey(resetKey);
    setRequestedPage(null);
    setAfter(null);
  }

  const variables = useMemo(
    () => ({
      space: spaceId,
      input: {
        predicates: [],
        orderBy: [orderBy],
        fullTextSearch,
        first,
        after,
        requestedPage,
      },
    }),
    [spaceId, orderBy, first, after, requestedPage, fullTextSearch]
  );

  const { error, loading, data, networkStatus, previousData } = usePolledQuery(
    API_KEY_SESSIONS_WITH_ACCESS_TO_SPACE,
    {
      variables,
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
    }
  );

  const { data: countData, previousData: previousCountData } = usePolledQuery(
    API_KEY_SESSIONS_WITH_ACCESS_TO_SPACE_COUNT,
    {
      variables: { space: spaceId, input: { fields: [], fullTextSearch, predicates: [] } },
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
    }
  );

  const loadApiKeys = useCallback(
    ({
      cursor,
      requestedPage,
    }: {
      cursor: string | null;
      requestedPage: SearchQueryRequestedPage | null;
    }) => {
      setAfter(cursor);
      setRequestedPage(requestedPage);
    },
    []
  );

  const initialLoading =
    loading && networkStatus === NetworkStatus.loading && !data?.apiKeySessionsWithAccessToSpace;

  const loadingContent =
    loading &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.fetchMore ||
      networkStatus === NetworkStatus.setVariables);

  const apiKeys = useMemo<SpaceDetailsApiKeySession[]>(() => {
    const edges =
      data?.apiKeySessionsWithAccessToSpace?.edges ||
      previousData?.apiKeySessionsWithAccessToSpace?.edges ||
      [];

    return edges.map(({ node }) => ({
      id: node.apiKey.id,
      name: node.apiKey.name,
      roleBindings: node.roles.map((grant) => ({
        // The backend collapses grants to one per role id today, so role.id alone would do; keying
        // on the origin too keeps this correct if that relaxes, and matches the IAM id shape.
        id: `${grant.origin}:${grant.role.id}`,
        role: { id: grant.role.id, name: grant.role.name, isSystem: grant.role.isSystem },
        origin: grant.origin,
      })),
    }));
  }, [
    data?.apiKeySessionsWithAccessToSpace?.edges,
    previousData?.apiKeySessionsWithAccessToSpace?.edges,
  ]);

  const totalCount =
    countData?.apiKeySessionsWithAccessToSpaceSuggestions.filteredCount ??
    previousCountData?.apiKeySessionsWithAccessToSpaceSuggestions.filteredCount ??
    0;

  return {
    loadApiKeys,
    error,
    apiKeys,
    initialLoading,
    loadingContent,
    totalCount,
    endCursor: data?.apiKeySessionsWithAccessToSpace?.pageInfo.endCursor,
    startCursor: data?.apiKeySessionsWithAccessToSpace?.pageInfo.startCursor,
    paginationResetKey: resetKey,
  };
};

export default useSpaceDetailsApiKeySessions;
